import { useMemo } from "react";
import type { ChatTask, VoiceTask } from "@/sdk";
import { getMostRecentTask, useSession } from "../../sessions";
import { isChatTask } from "../chat/typeGuards";
import { isVoiceTask } from "../voice/typeGuards";

/** Returns the most recent task from the current session */
export function useMostRecentTask() {
    const session = useSession();
    return getMostRecentTask(session);
}

/**
 * Enhanced hook that returns the most recent task along with type-specific information.
 * This optimizes type guard calls by running them once and caching the results.
 */
export function useTypedMostRecentTask() {
    const mostRecentTask = useMostRecentTask();

    return useMemo(() => {
        // Default values
        const result = {
            task: mostRecentTask,
            isVoice: false,
            isChat: false,
            voiceTask: undefined as VoiceTask | undefined,
            chatTask: undefined as ChatTask | undefined,
            customerInteractionId: undefined as string | undefined,
        };

        if (!mostRecentTask) return result;

        // Check and store type-specific task instances
        if (isVoiceTask(mostRecentTask)) {
            result.isVoice = true;
            result.voiceTask = mostRecentTask;
            result.customerInteractionId = mostRecentTask.asurionCallId;
        } else if (isChatTask(mostRecentTask)) {
            result.isChat = true;
            result.chatTask = mostRecentTask;
            result.customerInteractionId = mostRecentTask.chatId;
        }

        return result;
    }, [mostRecentTask]);
}
